import React, { useState } from 'react';
import { TotpMultiFactorGenerator } from 'firebase/auth';
import apiService from '../services/apiService';
import { useLanguage } from '../i18n';
import { standardErrorMessage } from '../utils/userError';
import {
    TOTP_FACTOR_ID,
    defaultHint,
    factorLabel,
    mfaErrorMessage,
    sanitizeCode,
} from '../utils/mfa';

const INPUT = 'block w-full h-11 px-3 mt-1 pai-input appearance-none focus:outline-none text-sm';
const PRIMARY = 'w-full h-11 px-4 text-sm font-semibold rounded-ctl transition-colors focus:outline-none bg-white text-pai-void hover:bg-white/90 disabled:bg-white/40 disabled:cursor-not-allowed';
const SECONDARY = 'w-full h-11 px-4 text-sm font-semibold text-white border border-pai bg-white/[0.05] rounded-ctl hover:bg-white/[0.1] hover:border-pai-strong transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';
const LINK = 'text-sm font-medium text-pai-blue hover:text-pai-blue-tint';

/**
 * The second step of a sign-in (2FA #333). Firebase refused the first factor
 * with `auth/multi-factor-auth-required` and handed us a `resolver`; this asks
 * for the code of one of the enrolled factors and completes the sign-in with it.
 *
 * Authenticator app (TOTP) only — SMS was removed (PO 2026-10-28). Only TOTP
 * hints are offered; an account whose only factor is a legacy phone one (none
 * exist in practice) gets a short message pointing to the recovery code.
 *
 * Used in two places with the SAME resolver contract:
 *   - LoginPage, for a password or Google sign-in (recovery codes allowed);
 *   - Settings → Security, when re-authenticating an MFA user before a
 *     sensitive change (`allowRecovery={false}` — a recovery code turns 2FA
 *     off, which is not what "confirm it's you" means there).
 *
 * `onResolved(userCredential)` fires once the resolver signs in. A recovery code
 * (`POST /auth/mfa/recover`) does NOT sign in: the backend removes every second
 * factor from the account, and `onRecovered({ email })` lets the caller sign in
 * again (it holds the password, when there was one).
 */
const MfaChallenge = ({
    resolver,
    email = '',
    allowRecovery = true,
    onResolved,
    onCancel,
    onRecovered,
}) => {
    const { t } = useLanguage();
    const hints = (resolver?.hints || []).filter((h) => h?.factorId === TOTP_FACTOR_ID);
    const [hint, setHint] = useState(() => defaultHint(hints));
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const [recovering, setRecovering] = useState(false);
    const [recoveryEmail, setRecoveryEmail] = useState(email || '');
    const [recoveryCode, setRecoveryCode] = useState('');
    const [recoveryError, setRecoveryError] = useState('');

    const codeReady = code.length === 6 && !!hint;

    const pickHint = (h) => {
        if (h === hint) return;
        setHint(h);
        setCode('');
        setError('');
    };

    const submitCode = async (e) => {
        e?.preventDefault?.();
        if (!codeReady || busy) return;
        setError('');
        setBusy(true);
        try {
            const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
            const cred = await resolver.resolveSignIn(assertion);
            onResolved?.(cred);
        } catch (err) {
            console.error('MFA sign-in failed:', err);
            setError(mfaErrorMessage(err, t));
            setBusy(false);
        }
        // On success the caller unmounts us (redirect / next step); staying
        // busy until then keeps a double submit from firing a second sign-in.
    };

    const submitRecovery = async (e) => {
        e?.preventDefault?.();
        const addr = recoveryEmail.trim();
        const rc = recoveryCode.trim();
        if (!addr || !rc || busy) return;
        setRecoveryError('');
        setBusy(true);
        try {
            // Unauthenticated, like `/auth/password-reset` in useAuth.
            await apiService.post('/auth/mfa/recover', { email: addr, code: rc });
            setBusy(false);
            onRecovered?.({ email: addr });
        } catch (err) {
            setRecoveryError(err?.body?.error?.type === 'invalid_recovery_code'
                ? t('mfaRecoveryInvalid', 'That recovery code is not valid.')
                : standardErrorMessage(t));
            setBusy(false);
        }
    };

    if (recovering) {
        return (
            <form data-testid="mfa-recovery" className="space-y-4" onSubmit={submitRecovery}>
                <div>
                    <h3 className="text-[18px] font-bold text-white">{t('mfaRecoveryTitle', 'Use a recovery code')}</h3>
                    <p className="mt-1 text-sm text-pai-meta">
                        {t('mfaRecoveryIntro', 'Enter one of the recovery codes you saved when you set up two-step verification. Using one turns two-step verification off for your account.')}
                    </p>
                </div>
                {!email && (
                    <div>
                        <label htmlFor="mfa-recovery-email" className="block text-sm font-medium text-pai-secondary">
                            {t('auxEmailAddress', 'Email address')}
                        </label>
                        <input
                            id="mfa-recovery-email"
                            data-testid="mfa-recovery-email"
                            type="email"
                            autoComplete="email"
                            value={recoveryEmail}
                            onChange={(e) => setRecoveryEmail(e.target.value)}
                            className={INPUT}
                        />
                    </div>
                )}
                <div>
                    <label htmlFor="mfa-recovery-input" className="block text-sm font-medium text-pai-secondary">
                        {t('mfaRecoveryLabel', 'Recovery code')}
                    </label>
                    <input
                        id="mfa-recovery-input"
                        data-testid="mfa-recovery-input"
                        type="text"
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        value={recoveryCode}
                        onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                        placeholder="ABCDE-FGHIJ"
                        className={`${INPUT} font-mono tracking-wider`}
                    />
                </div>
                {recoveryError && (
                    <div data-testid="mfa-recovery-error" role="alert" className="text-sm text-pai-red">{recoveryError}</div>
                )}
                <button
                    type="submit"
                    data-testid="mfa-recovery-submit"
                    disabled={busy || !recoveryCode.trim() || !recoveryEmail.trim()}
                    className={PRIMARY}
                >
                    {t('mfaRecoverySubmit', 'Use recovery code')}
                </button>
                <div className="flex items-center justify-between gap-3">
                    <button
                        type="button"
                        data-testid="mfa-recovery-back"
                        onClick={() => { setRecovering(false); setRecoveryError(''); }}
                        className={LINK}
                    >
                        {t('mfaUseCodeInstead', 'Use a verification code instead')}
                    </button>
                    {onCancel && (
                        <button type="button" data-testid="mfa-cancel" onClick={onCancel} className={LINK}>
                            {t('mfaBackToSignIn', 'Back to sign in')}
                        </button>
                    )}
                </div>
            </form>
        );
    }

    return (
        <form data-testid="mfa-challenge" data-factor={hint?.factorId || ''} className="space-y-4" onSubmit={submitCode}>
            <div>
                <h3 className="text-[18px] font-bold text-white">{t('mfaChallengeTitle', 'Two-step verification')}</h3>
                <p className="mt-1 text-sm text-pai-meta">
                    {t('mfaChallengeTotpIntro', 'Enter the 6-digit code from your authenticator app.')}
                </p>
            </div>

            {hints.length > 1 && (
                <div role="radiogroup" aria-label={t('mfaChooseMethod', 'Verification method')} className="space-y-2" data-testid="mfa-factor-options">
                    {hints.map((h) => {
                        const selected = h === hint;
                        return (
                            <button
                                key={h.uid}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                data-testid="mfa-factor-option"
                                data-factor={h.factorId}
                                data-selected={selected ? 'true' : 'false'}
                                onClick={() => pickHint(h)}
                                className={`w-full text-left px-3 py-2.5 rounded-ctl border text-sm transition-colors ${selected
                                    ? 'border-white/70 bg-white/[0.08] text-white'
                                    : 'border-pai bg-white/[0.03] text-pai-secondary hover:border-pai-strong hover:text-white'}`}
                            >
                                {factorLabel(h, t)}
                            </button>
                        );
                    })}
                </div>
            )}

            {!hint && (
                <div data-testid="mfa-no-totp" role="alert" className="text-sm text-pai-meta">
                    {allowRecovery
                        ? t('mfaNoTotpRecovery', 'This account has no authenticator app set up. Use one of your recovery codes to sign in.')
                        : t('mfaNoTotp', 'This account has no authenticator app set up, so it cannot be confirmed here.')}
                </div>
            )}

            {hint && (
                <div>
                    <label htmlFor="mfa-code-input" className="block text-sm font-medium text-pai-secondary">
                        {t('mfaCodeLabel', 'Verification code')}
                    </label>
                    <input
                        id="mfa-code-input"
                        data-testid="mfa-code-input"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(sanitizeCode(e.target.value))}
                        className={`${INPUT} font-mono tracking-[0.3em] tnum`}
                    />
                </div>
            )}

            {error && (
                <div data-testid="mfa-error" role="alert" className="p-3 text-sm text-pai-red border border-[rgba(240,68,56,0.4)] bg-[rgba(240,68,56,0.08)] rounded-ctl">
                    {error}
                </div>
            )}

            {hint && (
                <button type="submit" data-testid="mfa-submit" disabled={!codeReady || busy} className={PRIMARY}>
                    {busy ? t('mfaVerifying', 'Verifying…') : t('mfaVerify', 'Verify')}
                </button>
            )}

            <div className="flex items-center justify-between gap-3">
                {allowRecovery ? (
                    <button
                        type="button"
                        data-testid="mfa-use-recovery"
                        onClick={() => { setRecovering(true); setError(''); }}
                        className={LINK}
                    >
                        {t('mfaUseRecovery', 'Use a recovery code')}
                    </button>
                ) : <span />}
                {onCancel && (
                    <button type="button" data-testid="mfa-cancel" onClick={onCancel} className={LINK}>
                        {allowRecovery ? t('mfaBackToSignIn', 'Back to sign in') : t('commonCancel', 'Cancel')}
                    </button>
                )}
            </div>
        </form>
    );
};

export default MfaChallenge;
